import type { SseMessage } from '~/utils/agent-chat-sse';
import type { PageSnapshot } from '~/utils/page-snapshot';

/**
 * The client state of the chat assistant "Suge": one turn per question, built from the run events
 * of agent-api. pos-agent `docs/chat-assistant-v1.md` owns the event format and the limits that
 * this file repeats as constants.
 */

/** `message.text` of the run body has 1 to 4,000 characters. */
export const CHAT_MESSAGE_MAX_LENGTH = 4000;
const PAGE_PATH_MAX_LENGTH = 200;
const PAGE_TITLE_MAX_LENGTH = 100;

export interface ChatRunEvent {
  runId: string;
  /** The seq of a stored event. A live text delta has null. */
  seq: number | null;
  type: string;
  data: unknown;
}

export interface TicketProposal {
  title: string;
  summary: string;
}

/** One step of a page guide. A step without a target only opens its page. */
export interface GuideStep {
  path: string;
  target: string | null;
  text: string;
}

export type ChatRating = 'up' | 'down';
export type ChatRatingReason = 'wrong' | 'unclear' | 'not_what_i_asked';

export type ChatTurnStatus = 'pending' | 'streaming' | 'completed' | 'refused' | 'failed' | 'cancelled';
export type ChatIntent = 'answer' | 'ticket_proposal';

export interface ChatTurn {
  /** A local id. The run id comes with the first event. */
  id: string;
  question: string;
  intent: ChatIntent;
  runId: string | null;
  status: ChatTurnStatus;
  text: string;
  /** The text is the filtered final text of `GET /v1/runs/{id}`, not the streamed text. */
  final: boolean;
  tools: { id: string; name: string; done: boolean }[];
  proposal: TicketProposal | null;
  /** The page guide of the answer. A turn from the history has none, because the page can differ. */
  guide: GuideStep[] | null;
  /** The answer message, for the feedback endpoint. */
  messageId: string | null;
  /** The rating that agent-api accepted for this answer. */
  rating: { value: ChatRating; reason: ChatRatingReason | null } | null;
  /** `status` is the HTTP status of a request that failed, or 0 for a network error. */
  error: { code: string; message: string; status?: number } | null;
  /** No text came since the start or since the last tool. The progress line shows. */
  waiting: boolean;
  /** The seq of the last stored event, for `Last-Event-ID` on a reconnect. */
  lastSeq: number | null;
  /**
   * The length of `text` at the last stored event. The text after it comes from live deltas only.
   * A reconnect loses those deltas, and the stored text of the step arrives again in full.
   */
  stableLength: number;
}

export const ENDED_STATUSES: readonly ChatTurnStatus[] = ['completed', 'refused', 'failed', 'cancelled'];
export const isTurnEnded = (turn: Pick<ChatTurn, 'status'>): boolean => ENDED_STATUSES.includes(turn.status);

export function newChatTurn(id: string, question: string, intent: ChatIntent = 'answer'): ChatTurn {
  return {
    id,
    question,
    intent,
    runId: null,
    status: 'pending',
    text: '',
    final: false,
    tools: [],
    proposal: null,
    guide: null,
    messageId: null,
    rating: null,
    error: null,
    waiting: true,
    lastSeq: null,
    stableLength: 0,
  };
}

// ── Events ───────────────────────────────────────────────────────────────────

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const str = (value: unknown): string | null => (typeof value === 'string' ? value : null);

/** Reads the `data` of one SSE event: `{ run_id, seq, data }`. Other data gives null. */
export function parseChatRunEvent(message: SseMessage): ChatRunEvent | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(message.data);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || typeof parsed.run_id !== 'string') return null;
  const seq = typeof parsed.seq === 'number' && Number.isInteger(parsed.seq) ? parsed.seq : null;
  return { runId: parsed.run_id, seq, type: message.event, data: parsed.data };
}

const parseProposal = (spec: unknown): TicketProposal | null => {
  if (!isRecord(spec)) return null;
  const title = str(spec.title);
  const summary = str(spec.summary);
  return title !== null && summary !== null ? { title, summary } : null;
};

const parseGuide = (spec: unknown): GuideStep[] | null => {
  if (!isRecord(spec) || !Array.isArray(spec.steps)) return null;
  const steps: GuideStep[] = [];
  for (const step of spec.steps) {
    if (!isRecord(step)) return null;
    const path = str(step.path);
    const text = str(step.text);
    const target = step.target === null ? null : str(step.target);
    // The router opens the path, so it must be a path of this app. Browsers read `\` as `/`.
    if (path === null || !path.startsWith('/') || path.startsWith('//') || path.includes('\\') || text === null)
      return null;
    if (target === null && step.target !== null) return null;
    steps.push({ path, target, text });
  }
  return steps.length > 0 ? steps : null;
};

/** Applies one run event to a turn. It never changes the turn in place. */
export function reduceChatTurn(turn: ChatTurn, event: ChatRunEvent): ChatTurn {
  if (turn.runId !== null && event.runId !== turn.runId) return turn;
  // A replay after a reconnect can repeat a stored event.
  if (event.seq !== null && turn.lastSeq !== null && event.seq <= turn.lastSeq) return turn;

  const data = isRecord(event.data) ? event.data : {};
  let next: ChatTurn = { ...turn, runId: event.runId };
  if (next.status === 'pending') next.status = 'streaming';

  switch (event.type) {
    case 'message.delta': {
      const text = str(data.text) ?? '';
      next.text += text;
      if (text !== '') next.waiting = false;
      break;
    }
    case 'tool.started': {
      const id = str(data.tool_call_id);
      const name = str(data.name);
      if (id !== null && name !== null && !next.tools.some(tool => tool.id === id)) {
        next.tools = [...next.tools, { id, name, done: false }];
      }
      break;
    }
    case 'tool.completed': {
      const id = str(data.tool_call_id);
      next.tools = next.tools.map(tool => (tool.id === id ? { ...tool, done: true } : tool));
      next.waiting = true;
      break;
    }
    case 'artifact':
      if (data.kind === 'ticket_proposal') next.proposal = parseProposal(data.spec) ?? next.proposal;
      if (data.kind === 'ui_guide') next.guide = parseGuide(data.spec) ?? next.guide;
      break;
    case 'run.completed':
      if (data.refusal !== null && data.refusal !== undefined) {
        // The deltas of a refused response reached the stream, but they are not an answer.
        next = { ...next, status: 'refused', text: '', messageId: null };
      } else {
        next = { ...next, status: 'completed', messageId: str(data.message_id) };
      }
      break;
    case 'run.failed':
      next = {
        ...next,
        status: 'failed',
        error: { code: str(data.code) ?? 'failed', message: str(data.message) ?? '' },
      };
      break;
    case 'run.cancelled':
      next.status = 'cancelled';
      break;
    default:
      break;
  }

  if (event.seq !== null) {
    next.lastSeq = event.seq;
    next.stableLength = next.text.length;
  }
  return next;
}

/** Prepares a turn for the replay of a reconnect. The live text after the last stored event goes. */
export function resumeChatTurn(turn: ChatTurn): ChatTurn {
  return { ...turn, text: turn.text.slice(0, turn.stableLength) };
}

/**
 * The streamed text is not filtered. At the end, `result.text` of `GET /v1/runs/{id}` replaces it.
 * Without a final text, for example after a network error, the streamed text stays, and the
 * renderer still allows only help links.
 */
export function applyFinalText(turn: ChatTurn, text: string | null): ChatTurn {
  if (text === null || turn.status !== 'completed') return turn;
  return { ...turn, text, final: true };
}

const PROGRESS_TOOLS = new Set(['search_knowledge', 'get_document', 'propose_ticket', 'show_guide']);

/** The i18n key of the progress line, or null when the turn shows text or ended. */
export function chatProgressKey(turn: ChatTurn): string | null {
  if (isTurnEnded(turn)) return null;
  const running = turn.tools.filter(tool => !tool.done).at(-1);
  if (running) return PROGRESS_TOOLS.has(running.name) ? `chat.progress.${running.name}` : 'chat.progress.working';
  return turn.waiting ? 'chat.progress.thinking' : null;
}

// ── Request values ───────────────────────────────────────────────────────────

export interface ChatPageContext {
  page_path: string;
  page_title: string;
  page_snapshot?: PageSnapshot;
}

// agent-api refuses control characters (`\p{Cc}`), for example a newline or a tab, in the page fields.
const CONTROL_RE = /\p{Cc}+/gu;
const cleanField = (value: string, maxLength: number) =>
  value.replace(CONTROL_RE, ' ').replace(/\s+/g, ' ').trim().slice(0, maxLength).trim();

/**
 * The `context` of the run body: the page, and the snapshot of its structure when the caller gives
 * one. It holds no field value. A removed context chip sends nothing.
 */
export function chatContext(
  page: { path: string; title: string } | null,
  chipRemoved: boolean,
  snapshot?: PageSnapshot,
): ChatPageContext | undefined {
  if (chipRemoved || !page) return undefined;
  const path = cleanField(page.path, PAGE_PATH_MAX_LENGTH);
  if (path === '') return undefined;
  const title = cleanField(page.title, PAGE_TITLE_MAX_LENGTH) || cleanField(path, PAGE_TITLE_MAX_LENGTH);
  return { page_path: path, page_title: title, ...(snapshot && { page_snapshot: snapshot }) };
}

export function chatMessageText(text: string): string {
  return text.trim().slice(0, CHAT_MESSAGE_MAX_LENGTH);
}

// The suggestion chips of the empty state. Each group has its strings in `chat.suggestions`. Each
// chip asks a question that a page in `docs/` answers, because the chat answers from the help pages
// only. A page without such a help topic, for example `/support`, gets the general group.
const SUGGESTION_GROUPS: { group: string; prefixes: string[]; count: number }[] = [
  { group: 'reports', prefixes: ['/report-', '/receipts', '/hms-receipt-payments'], count: 3 },
  { group: 'menu', prefixes: ['/menu-items', '/condiments', '/courses', '/categories'], count: 3 },
  { group: 'printers', prefixes: ['/printers'], count: 2 },
  { group: 'devices', prefixes: ['/devices', '/table-maps', '/key-arrangements'], count: 2 },
];

export function chatSuggestionKeys(path: string): string[] {
  const match = SUGGESTION_GROUPS.find(({ prefixes }) =>
    prefixes.some(prefix =>
      prefix.endsWith('-') ? path.startsWith(prefix) : path === prefix || path.startsWith(`${prefix}/`),
    ),
  );
  const { group, count } = match ?? { group: 'general', count: 3 };
  return Array.from({ length: count }, (_, index) => `chat.suggestions.${group}.${index + 1}`);
}

// ── Errors ───────────────────────────────────────────────────────────────────

export interface ChatErrorInfo {
  /** The HTTP status, or 0 for a network error. */
  status: number;
  code: string;
}

export function chatErrorKey(error: ChatErrorInfo): string {
  switch (error.code) {
    case 'chat_assistant_disabled':
      return 'chat.errors.disabled';
    case 'chat_assistant_unavailable':
      return 'chat.errors.unavailable';
    case 'run_active':
      return 'chat.errors.run_active';
    case 'network':
      return 'chat.errors.network';
    case 'no_answer':
      return 'chat.errors.no_answer';
    default:
      return error.status === 503 ? 'chat.errors.unavailable' : 'chat.errors.generic';
  }
}

// ── The active run ───────────────────────────────────────────────────────────

/** The run that the panel reads. After a page reload, the panel opens its session and replays it. */
export interface ActiveChatRun {
  sessionId: string;
  runId: string;
  intent: ChatIntent;
}

type RunStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** A key for each user and shop, so that another user or another shop does not resume the run. */
export const activeRunKey = (userUid: string, shopUid: string | null | undefined): string =>
  `chat-active-run:${userUid}:${shopUid ?? ''}`;

export function readActiveRun(storage: RunStorage | null, key: string): ActiveChatRun | null {
  try {
    const value: unknown = JSON.parse(storage?.getItem(key) ?? 'null');
    if (!isRecord(value)) return null;
    const { sessionId, runId, intent } = value;
    if (typeof sessionId !== 'string' || typeof runId !== 'string') return null;
    if (intent !== 'answer' && intent !== 'ticket_proposal') return null;
    return { sessionId, runId, intent };
  } catch {
    return null;
  }
}

export function writeActiveRun(storage: RunStorage | null, key: string, run: ActiveChatRun): void {
  try {
    storage?.setItem(key, JSON.stringify(run));
  } catch {
    // A reload then opens an empty chat. The history still holds the session.
  }
}

export function clearActiveRun(storage: RunStorage | null, key: string): void {
  try {
    storage?.removeItem(key);
  } catch {
    // The next restore finds the run ended and removes nothing else.
  }
}
